import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { getRegistryRecord } from "@/lib/registry/registry.functions";
import {
  REGISTRY_BADGE_COPY,
  registryBadgeState,
  registryPath,
  registryUrl,
  type RegistryPopulationSnapshot,
  type RegistryTimelineEvent,
} from "@/lib/registry/registry";

import { RegistryBadge } from "@/components/registry/registry-badge";
import { RegistryQRCode } from "@/components/registry/registry-qr-code";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";

type RegistryListing = {
  id: string;
  slug: string | null;
  title: string;
  image: string | null;
  pricePence: number | null;
  endsAt: string | null;
};

function titleFor(r: {
  card_name: string | null;
  grade: string | null;
  provider: string;
  certificate_number: string;
}) {
  const name = r.card_name ?? "Graded card";
  return `${name}${r.grade ? ` · ${r.provider.toUpperCase()} ${r.grade}` : ""} · Cert ${r.certificate_number} | BidNova Registry`;
}

export const Route = createFileRoute("/registry/$provider/$cert")({
  loader: async ({ params }) => {
    const data = await getRegistryRecord({
      data: { provider: params.provider, cert: params.cert },
    });
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData, params }) => {
    const url = registryUrl(params.provider, params.cert);
    const record = loaderData?.record;
    const title = record
      ? titleFor(record)
      : `Certificate ${params.cert} | BidNova Verified Card Registry`;
    const description = record
      ? `Verification history, official source, market activity and population data for ${record.provider.toUpperCase()} certificate ${record.certificate_number}.`
      : "Permanent verification record for a graded trading card on BidNova Auctions.";
    const indexable = record?.status === "active" && record.verification_status === "verified";
    return {
      meta: [
        { title: title.slice(0, 70) },
        { name: "description", content: description.slice(0, 158) },
        { property: "og:title", content: title.slice(0, 70) },
        { property: "og:description", content: description.slice(0, 158) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(indexable ? [] : [{ name: "robots", content: "noindex, follow" }]),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: record
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                name: record.card_name ?? `Certificate ${record.certificate_number}`,
                sku: record.certificate_number,
                url,
                brand: { "@type": "Brand", name: record.provider.toUpperCase() },
                ...(record.set_name ? { model: record.set_name } : {}),
              }),
            },
          ]
        : [],
    };
  },
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
  component: RegistryRecordPage,
});

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-6 border-b border-border/40 py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

function money(pence: number | null | undefined) {
  if (pence == null) return null;
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

function RegistryRecordPage() {
  const data = Route.useLoaderData();
  const { record, timeline, population, market, estimate, listings, warnings, canonicalUrl } = data;
  const state = registryBadgeState(record);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:underline">
          Home
        </Link>{" "}
        / <span>Verified Card Registry</span>
      </nav>

      <header className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 sm:flex sm:flex-wrap sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-black sm:text-3xl">
            {record.card_name ?? `Certificate ${record.certificate_number}`}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {[record.year, record.set_name, record.card_number, record.variant, record.language]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        {record.grade && (
          <Badge variant="secondary" className="shrink-0 text-sm">
            {record.provider.toUpperCase()} {record.grade}
          </Badge>
        )}
      </header>

      <div className="mt-5">
        <RegistryBadge
          provider={record.provider}
          certNumber={record.certificate_number}
          status={record.status}
          verificationStatus={record.verification_status}
          officialUrl={record.official_source_url}
          lastVerifiedAt={record.last_verified_at}
          warnings={warnings}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          {REGISTRY_BADGE_COPY[state].detail} Permanent record:{" "}
          <span className="font-mono">{canonicalUrl}</span>
        </p>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-3 text-lg font-semibold">Certificate details</h2>
          <Row label="Grading company" value={record.provider.toUpperCase()} />
          <Row label="Certificate number" value={record.certificate_number} />
          <Row label="Grade" value={record.grade} />
          <Row label="Card" value={record.card_name} />
          <Row label="Set" value={record.set_name} />
          <Row label="Card number" value={record.card_number} />
          <Row label="Variant" value={record.variant} />
          <Row label="Language" value={record.language} />
          <Row label="Serial" value={record.serial_number} />
          <Row
            label="First verified"
            value={
              record.first_verified_at ? new Date(record.first_verified_at).toLocaleString() : null
            }
          />
          <Row
            label="Last checked"
            value={
              record.last_rechecked_at ? new Date(record.last_rechecked_at).toLocaleString() : null
            }
          />
          {record.official_source_url && (
            <a
              href={record.official_source_url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-3 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary underline underline-offset-4"
            >
              Official {record.provider.toUpperCase()} record
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-3 text-lg font-semibold">Permanent registry QR code</h2>
          <p className="mb-3 text-sm text-muted-foreground">
            Scan or share this code to open this verification record. The link never changes.
          </p>
          <RegistryQRCode
            path={registryPath(record.provider, record.certificate_number)}
            label={record.card_name ?? record.certificate_number}
          />
        </Card>



        <Card className="p-5">
          <h2 className="mb-3 text-lg font-semibold">Market activity</h2>
          {market && market.sampleSize > 0 ? (
            <>
              <Row label="Verified sales on record" value={market.sampleSize} />
              <Row label="Latest sale" value={money(market.latestSale)} />
              <Row label="Median sale" value={money(market.medianSale)} />
              <Row label="Highest sale" value={money(market.highestSale)} />
              <Row
                label="30-day change"
                value={market.change30d != null ? `${market.change30d}%` : null}
              />
              <Row label="Active BidNova listings" value={market.activeListings} />
              <p className="mt-3 text-xs text-muted-foreground">Source: {market.source}.</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No verified sales recorded for this certificate yet.
            </p>
          )}

          {estimate?.available ? (
            <div className="mt-4 rounded-lg border border-border/60 p-3">
              <div className="text-sm font-medium">
                Estimated value {money(estimate.low)} – {money(estimate.high)}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Estimate only, based on {estimate.comparableSales} comparable verified sales.
                Confidence {Math.round(estimate.confidence * 100)}%. This is not a guaranteed
                appraisal or an offer to buy.
              </p>
              <ul className="mt-2 list-disc pl-4 text-xs text-muted-foreground">
                {estimate.drivers.map((d: string) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            </div>
          ) : (
            estimate && (
              <p className="mt-4 text-xs text-muted-foreground">{estimate.reason}</p>
            )
          )}
        </Card>
      </div>

      {population.length > 0 && (
        <Card className="mt-6 p-5">
          <h2 className="mb-3 text-lg font-semibold">Official population</h2>
          <div className="space-y-2">
            {population.map((p: RegistryPopulationSnapshot) => (
              <div key={p.id} className="flex flex-wrap justify-between gap-3 text-sm">
                <span className="text-muted-foreground">
                  {new Date(p.captured_at).toLocaleDateString()} · {p.provider.toUpperCase()}
                  {p.grade ? ` ${p.grade}` : ""}
                </span>
                <span>
                  {p.population_same_grade != null && `${p.population_same_grade} at grade`}
                  {p.population_higher != null && ` · ${p.population_higher} higher`}
                  {p.population_total != null && ` · ${p.population_total} total`}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Population figures come from the grading company and are shown only when officially
            published.
          </p>
        </Card>
      )}

      {listings.length > 0 && (
        <Card className="mt-6 p-5">
          <h2 className="mb-3 text-lg font-semibold">Live BidNova listings</h2>
          <ul className="space-y-2">
            {listings.map((l: RegistryListing) => (
              <li key={l.id} className="flex items-center justify-between gap-4 text-sm">
                <Link
                  to="/auctions/$id"
                  params={{ id: l.slug ?? l.id }}
                  className="min-w-0 truncate font-medium hover:underline"
                >
                  {l.title}
                </Link>
                <span className="shrink-0 text-muted-foreground">{money(l.pricePence)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="mt-6 p-5">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck className="h-4 w-4 text-emerald-400" aria-hidden />
          Verification history
        </h2>
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">No verification events recorded yet.</p>
        ) : (
          <ol className="space-y-3">
            {timeline.map((e: RegistryTimelineEvent) => (
              <li key={e.id} className="border-l-2 border-border/60 pl-3 text-sm">
                <div className="text-xs text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()} · {e.event_type}
                </div>
                <div>{e.summary ?? e.status}</div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <p className="mt-8 text-xs text-muted-foreground">
        BidNova is not affiliated with PSA, BGS, CGC, SGC, ACE or TAG. Verification confirms that a
        certificate number matched the grading company's official record at the time shown; it is
        not an authentication or valuation of the physical card.
      </p>
    </main>
  );
}
